import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { FF_BLOCK_EDITING_ON_FAILING_SAVES } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSocket } from '@/hooks/useSocket';
import { isEditingBlocked, useWorkflowStore } from '@/state/workflow.state';
import { reportSocketHealth } from '@/utils/socketHealth';
import { useBatchesStore } from './batches.store';
import { isBatchDone } from './batch.utils';
import type { BatchRun } from '@/types/batch.types';
import type { ResultDeliveryContext } from './resultDiagnostics';

/** How long saves may stay held before editing is blocked: room for three 10s poll ticks. */
export const SAVE_HOLD_LIMIT_MS = 35_000;

/**
 * How late the hold limit may fire and still count as on time. Any later and the page was paused (a sleep, a frozen
 * tab) rather than waiting on the server, so the limit starts over instead of blocking.
 */
export const SAVE_HOLD_LIMIT_LATE_MS = 5_000;

/**
 * Delay before saving after a release, so the releasing update's results reach the canvas first. Autosave
 * runs only on a change, so held edits would otherwise wait for the next one.
 */
export const SAVE_AFTER_RELEASE_DELAY_MS = 2_000;

/** How many more checks, {@link SAVE_AFTER_RELEASE_DELAY_MS} apart, the release save waits out a save in flight. */
export const RELEASE_SAVE_MAX_WAITS = 5;

/**
 * `finished`: the batch ended while its final update was missed. `canceled`: the fetch was aborted, not sent, or
 * settled after the canvas unmounted.
 */
export type CatchUpOutcome = 'finished' | 'running' | 'failed' | 'canceled';

/** `read_only`: the tab came back to editor mode in place, as an embed does when it regains its edit lock. */
type HoldReason = 'disconnect' | 'load' | 'update_failed' | 'cancel' | 'read_only';

/** The updates that bring a batch's state from the server, so a release by one can be saved. */
const SERVER_UPDATES: ReadonlySet<HoldTrigger> = new Set(['socket', 'poll', 'catch_up', 'execute', 'cancel_refresh']);

/** Where a batch update came from, or what else re-evaluated the hold. */
type HoldTrigger =
  | ResultDeliveryContext['source']
  | 'update_failed'
  | 'disconnect'
  | 'batches_changed'
  | 'role_or_flag'
  | 'workflow_error'
  | 'workflow_changed';

interface Hold {
  workflowId: string;
  startedAt: number;
  reason: HoldReason;
  timer: number;
  /** When `timer` was set; a limit measured from the hold's start would read every restart as late. */
  armedAt: number;
  /** Limits that fired late and started over. */
  restarts: number;
  /** Polls and catch-ups for this workflow's runs that failed while held. */
  failedUpdates: number;
}

/** What the release save reports about the hold it follows. */
type ReleasedHold = Pick<Hold, 'workflowId' | 'reason'> & { skippedSaves: number };

/** A stretch in which a run's results may be missing from this editor tab, whether or not saves are held. */
interface StalePeriod {
  workflowId: string;
  startedAt: number;
  /** Saves sent during it. The hold turns these away, so they come from tabs without it. */
  savesSent: number;
  /** Results that finished on the server without this tab hearing, counted as the updates that end it arrive. */
  resultsMissed: number;
}

/** Node runs `incoming` reports finished that the stored batch doesn't: results this tab hadn't heard of. */
const countUnheardResults = (incoming: BatchRun): number => {
  const heard = new Set(
    (useBatchesStore.getState().batches[incoming.batchId]?.recipeRuns ?? [])
      .flatMap((run) => run.nodeRuns)
      .filter((nodeRun) => nodeRun.status === 'COMPLETED')
      .map((nodeRun) => nodeRun.id),
  );
  return incoming.recipeRuns
    .flatMap((run) => run.nodeRuns)
    .filter((nodeRun) => nodeRun.status === 'COMPLETED' && !heard.has(nodeRun.id)).length;
};

/** What could have released a held batch: its statuses, whether it's polled, and whether a cancel here awaits the server. */
const describeHeldBatch = (batch: BatchRun | undefined, polled: boolean, canceledLocally: boolean) => {
  const runStatuses: Record<string, number> = {};
  for (const run of batch?.recipeRuns ?? []) runStatuses[run.status] = (runStatuses[run.status] ?? 0) + 1;
  return {
    batchStatus: batch?.status ?? null,
    runStatuses,
    runningNodeRuns: (batch?.recipeRuns ?? [])
      .flatMap((run) => run.nodeRuns)
      .filter((nodeRun) => nodeRun.status === 'RUNNING').length,
    polled,
    canceledLocally,
  };
};

/** This workflow's batches still running, plus `canceledLocally` ones the server hasn't confirmed. */
const batchIdsToConfirm = (workflowId: string, canceledLocally: Set<string>): string[] =>
  Object.values(useBatchesStore.getState().batches)
    .filter((batch) => batch.recipeId === workflowId && (!isBatchDone(batch) || canceledLocally.has(batch.batchId)))
    .map((batch) => batch.batchId);

interface UseSaveHoldOptions {
  workflowId: string;
  /** Fetches one batch's current status and applies it. */
  catchUpBatch: (batchId: string) => Promise<CatchUpOutcome>;
  /** Saves the workflow as it is on the canvas; rejects when the save fails. */
  saveWorkflow: () => Promise<void>;
  /** Whether a poll is running for the batch. */
  isBatchPolled: (batchId: string) => boolean;
}

/**
 * Holds saves while a run's results may be missing from this client, since a save overwrites the whole
 * workflow. Only a run in progress can hold: this workflow's unfinished batches, plus one canceled here
 * with the socket down until the server confirms it. Nothing running, nothing held.
 *
 * Such a batch is held from a socket drop, a page load or a return to editor mode until its next server
 * update, and again after a failed poll or a cancel while the socket is down. A release saves; a hold past
 * {@link SAVE_HOLD_LIMIT_MS} blocks editing (`'Disconnected'`), timed afresh after the page was paused. Leaving
 * while held asks first.
 *
 * Without `block-editing-on-failing-saves` it holds nothing; it only reports the saves it would have held.
 *
 * @returns `noteBatchUpdate` (each server update, before it's merged), `noteBatchUpdateFailed` (a failed
 * poll or catch-up) and `noteBatchCanceled` (after a cancel succeeds; says whether the batch now waits
 * for the server).
 */
export const useSaveHold = ({
  workflowId,
  catchUpBatch,
  saveWorkflow,
  isBatchPolled,
}: UseSaveHoldOptions): {
  noteBatchUpdate: (batch: BatchRun, source: ResultDeliveryContext['source']) => void;
  noteBatchUpdateFailed: (batchId: string) => void;
  noteBatchCanceled: (batchId: string) => boolean;
} => {
  const isEnabled = useFeatureFlag(FF_BLOCK_EDITING_ON_FAILING_SAVES).value === true;
  const { addConnectionListener, getIsConnected } = useSocket();
  const isEditor = useWorkflowStore((state) => state.workflowRole === 'editor');

  /** Batches updated since the socket last dropped, or since the page loaded. */
  const freshBatchIds = useRef(new Set<string>());
  /** Batches canceled here with the socket down whose server state hasn't arrived since. */
  const canceledLocallyIds = useRef(new Set<string>());
  const hold = useRef<Hold | null>(null);
  const stalePeriod = useRef<StalePeriod | null>(null);
  const hasConnected = useRef(false);
  const hasDisconnected = useRef(false);
  const isConnected = useRef(false);
  /** Why the next hold starts, when it isn't a drop or a load. */
  const nextHoldReason = useRef<HoldReason | null>(null);
  const releaseSaveTimer = useRef<number | undefined>(undefined);
  /** A status request can settle after the canvas unmounts; a hold it started would outlive its workflow. */
  const isUnmounted = useRef(false);

  const latest = useRef({ workflowId, catchUpBatch, saveWorkflow, isBatchPolled, getIsConnected, isEnabled, isEditor });
  latest.current = { workflowId, catchUpBatch, saveWorkflow, isBatchPolled, getIsConnected, isEnabled, isEditor };

  const setHeld = (held: boolean) => useWorkflowStore.setState({ isSaveHeld: held });

  const staleBatchCount = (workflowId: string) =>
    batchIdsToConfirm(workflowId, canceledLocallyIds.current).filter((batchId) => !freshBatchIds.current.has(batchId))
      .length;

  /** The batches a hold still waits on, as an escalation reports them. */
  const describeHeldBatches = (workflowId: string) =>
    batchIdsToConfirm(workflowId, canceledLocallyIds.current)
      .filter((batchId) => !freshBatchIds.current.has(batchId))
      .map((batchId) =>
        describeHeldBatch(
          useBatchesStore.getState().batches[batchId],
          latest.current.isBatchPolled(batchId),
          canceledLocallyIds.current.has(batchId),
        ),
      );

  /** What every hold report carries, so holds can be split by how they started and how they went. */
  const holdContext = (current: Hold) => ({
    workflowId: current.workflowId,
    reason: current.reason,
    holdMs: Date.now() - current.startedAt,
    socketConnected: isConnected.current,
    failedUpdates: current.failedUpdates,
    skippedSaves: useWorkflowStore.getState().heldSaveCount,
    restarts: current.restarts,
  });

  /** Reports a period whose saves could have overwritten results; where the hold is on, one means a save got past it. */
  const endStalePeriod = (endedBy: HoldTrigger | 'left') => {
    const period = stalePeriod.current;
    stalePeriod.current = null;
    if (!period || period.savesSent === 0) return;
    reportSocketHealth('workflow_save_sent_while_results_missing', {
      workflowId: period.workflowId,
      saveHoldEnabled: latest.current.isEnabled,
      savesSent: period.savesSent,
      resultsMissed: period.resultsMissed,
      periodMs: Date.now() - period.startedAt,
      afterDrop: hasDisconnected.current,
      socketConnected: isConnected.current,
      endedBy,
    });
  };

  /**
   * Saves what the hold skipped; reports `sent`, `failed`, `blocked`, `busy` (a save stayed in flight) or
   * `not_editor` (the tab left editor mode, so nothing went out).
   */
  const saveAfterRelease = useCallback((released: ReleasedHold, waits = 0): void => {
    const { workflowError, workflowRole, _saveStartedAt: saveInFlightSince } = useWorkflowStore.getState();
    const reportSave = (context: Record<string, unknown>) =>
      reportSocketHealth('workflow_save_hold_release_save', { ...released, waits, ...context });
    if (isEditingBlocked(workflowError)) {
      reportSave({ outcome: 'blocked', blockedBy: workflowError });
    } else if (workflowRole !== 'editor') {
      reportSave({ outcome: 'not_editor' });
    } else if (saveInFlightSince !== null) {
      if (waits < RELEASE_SAVE_MAX_WAITS) {
        releaseSaveTimer.current = window.setTimeout(
          () => saveAfterRelease(released, waits + 1),
          SAVE_AFTER_RELEASE_DELAY_MS,
        );
      } else {
        reportSave({ outcome: 'busy' });
      }
    } else {
      latest.current.saveWorkflow().then(
        () => reportSave({ outcome: 'sent' }),
        () => reportSave({ outcome: 'failed' }),
      );
    }
  }, []);

  const escalate = useCallback(() => {
    const current = hold.current;
    if (!current) return;
    // Long past due, the page wasn't running to hear from the server; on resume it gets a full window to reconnect.
    if (Date.now() - current.armedAt >= SAVE_HOLD_LIMIT_MS + SAVE_HOLD_LIMIT_LATE_MS) {
      current.armedAt = Date.now();
      current.restarts += 1;
      current.timer = window.setTimeout(escalate, SAVE_HOLD_LIMIT_MS);
      return;
    }
    hold.current = null;
    const { workflowError, setWorkflowError } = useWorkflowStore.getState();
    reportSocketHealth('workflow_save_hold_escalated', {
      ...holdContext(current),
      staleBatches: staleBatchCount(current.workflowId),
      heldBatches: describeHeldBatches(current.workflowId),
    });
    if (!isEditingBlocked(workflowError)) setWorkflowError('Disconnected');
    // The block stops saves on its own; letting go of the hold lets its Refresh skip the leave prompt.
    setHeld(false);
  }, []);

  const evaluate = useCallback(
    (trigger: HoldTrigger) => {
      if (isUnmounted.current) return;
      const { workflowId, isEditor: editor, isEnabled: enabled } = latest.current;
      const isStale = batchIdsToConfirm(workflowId, canceledLocallyIds.current).some(
        (batchId) => !freshBatchIds.current.has(batchId),
      );
      const { workflowError } = useWorkflowStore.getState();
      const isBlocked = isEditingBlocked(workflowError);
      const shouldHold = enabled && editor && isStale && !isBlocked;
      if (shouldHold && !hold.current) {
        clearTimeout(releaseSaveTimer.current);
        hold.current = {
          workflowId,
          startedAt: Date.now(),
          reason: nextHoldReason.current ?? (hasDisconnected.current ? 'disconnect' : 'load'),
          timer: window.setTimeout(escalate, SAVE_HOLD_LIMIT_MS),
          armedAt: Date.now(),
          restarts: 0,
          failedUpdates: 0,
        };
        useWorkflowStore.setState({ isSaveHeld: true, heldSaveCount: 0 });
        reportSocketHealth('workflow_save_hold_started', {
          workflowId,
          reason: hold.current.reason,
          socketConnected: isConnected.current,
          staleBatches: staleBatchCount(workflowId),
          batches: batchIdsToConfirm(workflowId, canceledLocallyIds.current).length,
        });
      } else if (!shouldHold && hold.current) {
        const ended = hold.current;
        hold.current = null;
        clearTimeout(ended.timer);
        if (isBlocked) {
          reportSocketHealth('workflow_save_hold_blocked', { ...holdContext(ended), blockedBy: workflowError });
        } else {
          const releasedContext = holdContext(ended);
          reportSocketHealth('workflow_save_hold_released', {
            ...releasedContext,
            releasedBy: trigger,
            resultsMissed: stalePeriod.current?.resultsMissed ?? 0,
          });
          // Other releases bring no results, so the canvas could still be missing them.
          if (SERVER_UPDATES.has(trigger)) {
            const released = {
              workflowId: ended.workflowId,
              reason: ended.reason,
              skippedSaves: releasedContext.skippedSaves,
            };
            releaseSaveTimer.current = window.setTimeout(() => saveAfterRelease(released), SAVE_AFTER_RELEASE_DELAY_MS);
          }
        }
        setHeld(false);
      }
      const isAtRisk = editor && isStale;
      if (isAtRisk && !stalePeriod.current) {
        stalePeriod.current = { workflowId, startedAt: Date.now(), savesSent: 0, resultsMissed: 0 };
      } else if (!isAtRisk && stalePeriod.current) {
        endStalePeriod(trigger);
      }
    },
    [escalate, saveAfterRelease],
  );

  const noteBatchUpdate = useCallback(
    (batch: BatchRun, source: ResultDeliveryContext['source']) => {
      const { batchId } = batch;
      if (stalePeriod.current && !freshBatchIds.current.has(batchId)) {
        stalePeriod.current.resultsMissed += countUnheardResults(batch);
      }
      freshBatchIds.current.add(batchId);
      canceledLocallyIds.current.delete(batchId);
      evaluate(source);
    },
    [evaluate],
  );

  const noteBatchUpdateFailed = useCallback(
    (batchId: string) => {
      if (hold.current) hold.current.failedUpdates += 1;
      // With the socket up, its updates still arrive; a failed poll only matters without it.
      if (isConnected.current || !freshBatchIds.current.has(batchId)) return;
      freshBatchIds.current.delete(batchId);
      nextHoldReason.current = 'update_failed';
      evaluate('update_failed');
      nextHoldReason.current = null;
    },
    [evaluate],
  );

  const noteBatchCanceled = useCallback(
    (batchId: string) => {
      if (isUnmounted.current || !latest.current.isEnabled) return false;
      // With the socket up, its updates bring the run's state, but only once this tab has caught up on the run.
      if (isConnected.current && freshBatchIds.current.has(batchId)) return false;
      freshBatchIds.current.delete(batchId);
      canceledLocallyIds.current.add(batchId);
      nextHoldReason.current = 'cancel';
      evaluate('cancel');
      nextHoldReason.current = null;
      return true;
    },
    [evaluate],
  );

  useEffect(() => {
    const catchUp = async (trigger: 'first_connect' | 'reconnect') => {
      const { workflowId, catchUpBatch: fetchBatch, isEnabled: enabled, isEditor: editor } = latest.current;
      const batchIds = batchIdsToConfirm(workflowId, canceledLocallyIds.current);
      if (!enabled || !editor || batchIds.length === 0) return;
      const startedAt = Date.now();
      const wasHeld = hold.current !== null;
      const outcomes = await Promise.all(batchIds.map((batchId) => fetchBatch(batchId)));
      reportSocketHealth('workflow_socket_catch_up', {
        workflowId,
        trigger,
        durationMs: Date.now() - startedAt,
        wasHeld,
        stillHeld: hold.current !== null,
        batches: batchIds.length,
        finished: outcomes.filter((outcome) => outcome === 'finished').length,
        failed: outcomes.filter((outcome) => outcome === 'failed').length,
      });
    };

    const onConnectionChange = (connected: boolean) => {
      isConnected.current = connected;
      if (!connected) {
        hasDisconnected.current = true;
        freshBatchIds.current.clear();
        evaluate('disconnect');
        return;
      }
      const trigger = hasConnected.current ? 'reconnect' : 'first_connect';
      hasConnected.current = true;
      void catchUp(trigger);
    };

    if (latest.current.getIsConnected()) onConnectionChange(true);
    return addConnectionListener(onConnectionChange);
  }, [addConnectionListener, evaluate]);

  // A layout effect, like the teardown below. Leaving the workflow, Recipe's passive cleanup empties the stores this
  // hook subscribes to, and React runs a parent's passive cleanup before its children's. Layout cleanups all run
  // earlier, so the hook stops listening before that reset reaches it.
  useLayoutEffect(() => {
    isUnmounted.current = false;
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  // Runs appear (a reload's rehydration) and finish through the store.
  useEffect(() => useBatchesStore.subscribe(() => evaluate('batches_changed')), [evaluate]);

  useEffect(
    () =>
      useWorkflowStore.subscribe((state, previous) => {
        if (state.workflowError !== previous.workflowError) evaluate('workflow_error');
      }),
    [evaluate],
  );

  useEffect(
    () =>
      useWorkflowStore.subscribe((state, previous) => {
        const isSaveSent = state._saveStartedAt !== null && previous._saveStartedAt === null;
        const period = stalePeriod.current;
        if (isSaveSent && period && period.workflowId === state.recipe?.id) period.savesSent += 1;
      }),
    [],
  );

  // Out of editor mode the socket is closed without a drop and no status is asked for, so the tab comes back
  // knowing nothing current about its runs, as after a reconnect.
  const wasEditor = useRef(isEditor);
  useEffect(() => {
    if (isEditor === wasEditor.current) return;
    wasEditor.current = isEditor;
    if (!isEditor) {
      isConnected.current = false;
      freshBatchIds.current.clear();
      return;
    }
    nextHoldReason.current = 'read_only';
    evaluate('role_or_flag');
    nextHoldReason.current = null;
  }, [isEditor, evaluate]);

  useEffect(() => {
    evaluate('role_or_flag');
  }, [isEditor, isEnabled, evaluate]);

  // A new workflow starts with nothing known; a hold left behind would stop its saves.
  useEffect(() => {
    freshBatchIds.current = new Set();
    canceledLocallyIds.current = new Set();
    hasDisconnected.current = false;
    evaluate('workflow_changed');
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [workflowId]);

  // A layout cleanup, so it runs before Recipe's reset (see above) and reports the hold with its real skipped saves.
  useLayoutEffect(
    () => () => {
      clearTimeout(releaseSaveTimer.current);
      endStalePeriod('left');
      if (hold.current) {
        clearTimeout(hold.current.timer);
        // Navigated away within the app while held; the skipped saves never went out.
        reportSocketHealth('workflow_save_hold_abandoned', holdContext(hold.current));
      }
      hold.current = null;
      setHeld(false);
    },
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
    [workflowId],
  );

  const isSaveHeld = useWorkflowStore((state) => state.isSaveHeld);
  useEffect(() => {
    if (!isSaveHeld) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // A block's banner already says to refresh; a prompt on top of it would contradict it.
      if (!hold.current || isEditingBlocked(useWorkflowStore.getState().workflowError)) return;
      // Sent before the prompt, so whether the user then left shows as a missing release or escalation.
      reportSocketHealth('workflow_save_hold_leave_prompt', holdContext(hold.current));
      event.preventDefault();
      // Older browsers show the prompt only when this is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isSaveHeld]);

  return { noteBatchUpdate, noteBatchUpdateFailed, noteBatchCanceled };
};
